import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FiSearch } from 'react-icons/fi';
import { Check, OplaEmpty, OplaId, OplaPanel, OplaTable, Swatch, parseJson } from './OplaPrimitives';

// Read-only views of what "Sync from Opla" pulled down. They are not editable
// on purpose: the source of truth is Opla, and typing into a synced blob only
// produces a difference the next sync silently discards.

const DATA_TYPE_TONE = {
  TEXT:          'tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200',
  NUMBER:        'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
  SINGLE_OPTION: 'tw-bg-purple-50 tw-text-purple-700 tw-border-purple-200',
  MULTI_OPTION:  'tw-bg-pink-50 tw-text-pink-700 tw-border-pink-200',
};

export const OplaCustomFieldsViewer = ({ value }) => {
  const [search, setSearch] = useState('');
  const fields = useMemo(() => parseJson(value, []), [value]);

  const q = search.toLowerCase();
  const filtered = fields.filter((f) => !q || (f.name || '').toLowerCase().includes(q));

  if (!fields.length) return <OplaEmpty>No custom fields synced yet.</OplaEmpty>;

  const columns = [
    { header: 'Name', width: '40%', truncate: true, cell: (f) => <span className="tw-font-medium tw-text-slate-800">{f.name || '—'}</span> },
    {
      header: 'Type', width: '25%',
      cell: (f) => (
        <span className={`tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-semibold tw-border ${DATA_TYPE_TONE[f.data_type] || 'tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200'}`}>
          {f.data_type || '—'}
        </span>
      ),
    },
    { header: 'Required', width: '15%', align: 'center', cell: (f) => <Check on={!!f.is_required} /> },
    { header: 'Read-only', width: '20%', align: 'center', cell: (f) => <Check on={!!f.is_readonly} /> },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-relative">
        <FiSearch size={12} className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name…"
          className="tw-w-full tw-pl-7 tw-pr-3 tw-py-1.5 tw-text-xs tw-rounded-lg tw-border tw-border-slate-200 tw-outline-none focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100 tw-bg-white"
        />
      </div>
      <OplaPanel footer={`${filtered.length} / ${fields.length} fields`}>
        <OplaTable columns={columns} rows={filtered} rowKey={(f, i) => f.id || i} />
        {filtered.length === 0 && (
          <div className="tw-py-4 tw-text-center tw-text-xs tw-text-slate-400">No results for “{search}”</div>
        )}
      </OplaPanel>
    </div>
  );
};
OplaCustomFieldsViewer.propTypes = { value: PropTypes.string };

// ── Contract types / task types / related-party roles ────────────────────────
// Three lists with the same shape; one component, three column specs.

const ORDER_CELL = (row, i) => <span className="tw-font-mono tw-text-slate-400">{row.order_number ?? i + 1}</span>;
const NAME_CELL  = (row) => <span className="tw-font-medium tw-text-slate-800">{row.name || '—'}</span>;

const LIST_VIEWS = {
  contract_type: {
    empty: 'No contract types synced yet. Use “Sync from Opla”.',
    noun: 'types',
    columns: [
      { header: '#', width: '3rem', cell: ORDER_CELL },
      { header: 'Name', cell: NAME_CELL },
      { header: 'Opla ID', width: '42%', truncate: true, cell: (r) => <OplaId id={r.id} /> },
      { header: 'Active', width: '4.5rem', align: 'center', cell: (r) => <Check on={!!r.is_active} /> },
    ],
  },
  task_type: {
    empty: 'No task types synced yet. Use “Sync from Opla”.',
    noun: 'types',
    columns: [
      { header: '#', width: '3rem', cell: ORDER_CELL },
      { header: 'Name', cell: NAME_CELL },
      { header: 'Color', width: '9rem', cell: (r) => <Swatch color={r.color} /> },
      { header: 'Opla ID', width: '32%', truncate: true, cell: (r) => <OplaId id={r.id} /> },
      { header: 'Active', width: '4.5rem', align: 'center', cell: (r) => <Check on={!!r.is_active} /> },
    ],
  },
  related_party_role: {
    empty: 'No related-party roles synced yet. Use “Sync from Opla”.',
    noun: 'roles',
    columns: [
      { header: '#', width: '3rem', cell: (r, i) => <span className="tw-font-mono tw-text-slate-400">{i + 1}</span> },
      { header: 'Role', cell: NAME_CELL },
      { header: 'Color', width: '9rem', cell: (r) => <Swatch color={r.color} /> },
      { header: 'Opla ID', width: '42%', truncate: true, cell: (r) => <OplaId id={r.id} /> },
    ],
  },
};

export const OplaListViewer = ({ value, view }) => {
  const items = useMemo(() => parseJson(value, []), [value]);
  const spec = LIST_VIEWS[view];

  if (!items.length) return <OplaEmpty>{spec.empty}</OplaEmpty>;

  return (
    <OplaPanel footer={`${items.length} ${spec.noun}`}>
      <OplaTable columns={spec.columns} rows={items} rowKey={(r, i) => r.id || i} />
    </OplaPanel>
  );
};
OplaListViewer.propTypes = {
  value: PropTypes.string,
  view: PropTypes.oneOf(Object.keys(LIST_VIEWS)).isRequired,
};

// ── Sales process ─────────────────────────────────────────────────────────────

const STAGE_TONE = {
  OPEN:       'tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200',
  CLOSE_WON:  'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200',
  CLOSE_LOST: 'tw-bg-red-50 tw-text-red-700 tw-border-red-200',
};

export const OplaSalesProcessViewer = ({ value }) => {
  const process = useMemo(() => parseJson(value, {}), [value]);
  const stages = process.stages || [];

  if (!stages.length) return <OplaEmpty>No sales process synced yet.</OplaEmpty>;

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {process.name && (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-font-semibold tw-text-slate-700">{process.name}</span>
          {process.is_active && (
            <span className="tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-emerald-200">
              Active
            </span>
          )}
        </div>
      )}
      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-divide-y tw-divide-slate-100 tw-bg-white">
        {stages.map((stage, i) => (
          <div key={stage.id || i} className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 hover:tw-bg-slate-50/70 tw-transition-colors">
            <span className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-flex-shrink-0" style={{ backgroundColor: stage.color || '#94a3b8' }} />
            <span className="tw-text-[11px] tw-tabular-nums tw-text-slate-400 tw-w-4 tw-flex-shrink-0">{stage.order_number}</span>
            <span className="tw-flex-1 tw-text-xs tw-font-medium tw-text-slate-800 tw-truncate">{stage.name}</span>
            {stage.status && (
              <span className={`tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-flex-shrink-0 ${STAGE_TONE[stage.status] || 'tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200'}`}>
                {stage.status.replace('_', ' ')}
              </span>
            )}
            <span className="tw-text-[11px] tw-text-slate-400 tw-flex-shrink-0 tw-w-10 tw-text-right tw-tabular-nums">{stage.weight}%</span>
          </div>
        ))}
      </div>
      <p className="tw-text-[11px] tw-text-slate-400 tw-text-right tw-mb-0">{stages.length} stages</p>
    </div>
  );
};
OplaSalesProcessViewer.propTypes = { value: PropTypes.string };

// Which viewer (if any) owns a key — keeps the switch out of the row component.
export const oplaViewerFor = (key, value) => {
  switch (key) {
    case 'OPLA_CUSTOM_FIELDS':       return <OplaCustomFieldsViewer value={value} />;
    case 'OPLA_SALES_PROCESS':       return <OplaSalesProcessViewer value={value} />;
    case 'OPLA_CONTRACT_TYPE':       return <OplaListViewer value={value} view="contract_type" />;
    case 'OPLA_TASK_TYPE':           return <OplaListViewer value={value} view="task_type" />;
    case 'OPLA_RELATED_PARTY_ROLE':  return <OplaListViewer value={value} view="related_party_role" />;
    default:                         return null;
  }
};
